/**
 * Take (Instant Video → "Take"), phase 1 — the item-side reading of a take
 * request. A take is a request to record a to-camera video for a tended
 * item with the teleprompter; the backend keeps it as a flag on the item
 * (tendv2_content.take_requested_at & co.), orthogonal to queued/published.
 *
 *   none       no request on the item
 *   requested  asked for, nothing recorded since
 *   recorded   the recorder's Save landed after the request (the clip is on
 *              the item as media; the item stays in the Take queue until it
 *              is queued to a channel)
 */

export type TakeState = 'none' | 'requested' | 'recorded';

export interface TakeFields {
  take_requested_at?: string | null;
  take_source?: 'tactic' | 'manual' | string | null;
  take_requested_by?: string | null;
  take_brand_id?: number | null;
  take_recorded_at?: string | null;
}

const ms = (iso?: string | null): number => {
  if (!iso) return NaN;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? NaN : t;
};

export const takeState = (item: TakeFields | null | undefined): TakeState => {
  if (!item?.take_requested_at) return 'none';
  const requested = ms(item.take_requested_at);
  const recorded = ms(item.take_recorded_at);
  // A recording older than the request is from before a cleared request —
  // this ask has not been recorded yet.
  if (!Number.isNaN(recorded) && (Number.isNaN(requested) || recorded >= requested)) return 'recorded';
  return 'requested';
};

/** The five take_* fields off any item payload, for lifting into state. */
export const pickTakeFields = (item: TakeFields | null | undefined): TakeFields => ({
  take_requested_at: item?.take_requested_at ?? null,
  take_source: item?.take_source ?? null,
  take_requested_by: item?.take_requested_by ?? null,
  take_brand_id: item?.take_brand_id ?? null,
  take_recorded_at: item?.take_recorded_at ?? null,
});

/** The list-row tag's copy per state. (The card notch says "Take" in
 * both visible states and shows recorded as a check glyph + live ring —
 * a longer word overflowed the 350px card header's left column.) */
export const takeLabel = (state: TakeState): string =>
  state === 'recorded' ? 'Take · rec' : state === 'requested' ? 'Take' : '';

/** Window event fired by any control that changes an item's take request,
 * so every mounted view of the item (card, list row, Take strip) agrees
 * without a refetch. detail: { contentId, take: TakeFields }. */
export const TAKE_CHANGED_EVENT = 'tend:take-changed';

export interface TakeChangedDetail {
  contentId: number;
  take: TakeFields;
}

export const announceTakeChange = (contentId: number, take: TakeFields): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<TakeChangedDetail>(TAKE_CHANGED_EVENT, { detail: { contentId, take } }));
};

/** Window event fired when a TEND run finishes (prune or branch): new
 * tended items exist, some of them takes (a `take: true` tactic, or the
 * picker's row toggle). The Take strip refetches on it so a fresh take
 * shows up without a page refresh. detail: { contentId, transformationId }. */
export const TEND_RUN_COMPLETED_EVENT = 'tend:run-completed';

export interface TendRunCompletedDetail {
  contentId: number;
  transformationId: number | null;
}

export const announceTendRun = (contentId: number, transformationId: number | null): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<TendRunCompletedDetail>(TEND_RUN_COMPLETED_EVENT, {
    detail: { contentId, transformationId },
  }));
};
